import { useParams, Link } from 'react-router-dom';
import { getComicsByCategory, getCategoryBySlug, categories } from '@/mocks/comics';
import { ComicCard } from '@/pages/home/components/ComicCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';

const categoryHeroImages: Record<string, string> = {
  romance: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20romance%20genre%20banner%2C%20manga%20style%20illustration%2C%20warm%20atmospheric%20lighting%2C%20beautiful%20composition%2C%20clean%20anime%20art%2C%20couple%20in%20cherry%20blossom%20scene&width=1400&height=560&seq=cat-hero-romance&orientation=landscape',
  fantasy: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20fantasy%20genre%20banner%2C%20manga%20style%20illustration%2C%20magical%20adventure%20scene%2C%20warm%20atmospheric%20lighting%2C%20beautiful%20composition%2C%20clean%20anime%20art&width=1400&height=560&seq=cat-hero-fantasy&orientation=landscape',
  action: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20action%20genre%20banner%2C%20manga%20style%20illustration%2C%20dynamic%20battle%20scene%2C%20dramatic%20lighting%2C%20beautiful%20composition%2C%20clean%20anime%20art&width=1400&height=560&seq=cat-hero-action&orientation=landscape',
  mystery: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20mystery%20genre%20banner%2C%20manga%20style%20illustration%2C%20noir%20detective%20scene%2C%20suspenseful%20lighting%2C%20beautiful%20composition%2C%20clean%20anime%20art&width=1400&height=560&seq=cat-hero-mystery&orientation=landscape',
  urban: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20urban%20life%20genre%20banner%2C%20manga%20style%20illustration%2C%20modern%20city%20scene%2C%20warm%20sunset%20lighting%2C%20beautiful%20composition%2C%20clean%20anime%20art&width=1400&height=560&seq=cat-hero-urban&orientation=landscape',
  comedy: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20comedy%20genre%20banner%2C%20manga%20style%20illustration%2C%20funny%20colorful%20scene%2C%20bright%20playful%20lighting%2C%20beautiful%20composition%2C%20clean%20anime%20art&width=1400&height=560&seq=cat-hero-comedy&orientation=landscape',
  bl: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20BL%20genre%20banner%2C%20manga%20style%20illustration%2C%20elegant%20romantic%20scene%2C%20soft%20dreamy%20lighting%2C%20beautiful%20composition%2C%20clean%20anime%20art&width=1400&height=560&seq=cat-hero-bl&orientation=landscape',
  horror: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20horror%20genre%20banner%2C%20manga%20style%20illustration%2C%20dark%20eerie%20scene%2C%20creepy%20atmospheric%20lighting%2C%20beautiful%20composition%2C%20clean%20anime%20art&width=1400&height=560&seq=cat-hero-horror&orientation=landscape',
  scifi: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20sci-fi%20genre%20banner%2C%20manga%20style%20illustration%2C%20futuristic%20cityscape%2C%20neon%20lighting%2C%20beautiful%20composition%2C%20clean%20anime%20art&width=1400&height=560&seq=cat-hero-scifi&orientation=landscape',
  historical: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20historical%20genre%20banner%2C%20manga%20style%20illustration%2C%20ancient%20palace%20scene%2C%20traditional%20warm%20lighting%2C%20beautiful%20composition%2C%20clean%20anime%20art&width=1400&height=560&seq=cat-hero-historical&orientation=landscape',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = getCategoryBySlug(slug || '');
  const comics = getComicsByCategory(slug || '');

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-heading text-foreground-800 mb-2">分类不存在</h2>
          <Link to="/" className="text-sm text-primary-500 hover:text-primary-600">返回首页</Link>
        </div>
      </div>
    );
  }

  const heroImage = categoryHeroImages[category.slug] || categoryHeroImages.romance;

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'http://lvtianyuan.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: '分类浏览',
        item: 'http://lvtianyuan.com/category',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: category.name,
        item: `http://lvtianyuan.com/category/${category.slug}`,
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${category.name}韩国漫画_${category.name}韩漫大全_妖精漫画网`}
        description={`妖精漫画网${category.name}分类，收录${category.comicCount}部精选${category.name}韩国漫画。${category.description}每日更新，免费在线观看。`}
        keywords={`${category.name}韩国漫画,${category.name}韩漫,${category.name}漫画在线观看,妖精漫画网`}
        canonicalUrl={`http://lvtianyuan.com/category/${category.slug}`}
        ogImage="https://readdy.ai/api/search-image?query=Korean%20manhwa%20collection%20banner%2C%20colorful%20manga%20covers%20collage%2C%20warm%20lighting%2C%20library%20atmosphere&width=1200&height=630&seq=og-category&orientation=landscape"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${category.name} - 妖精漫画网`,
            description: category.description,
            url: `http://lvtianyuan.com/category/${category.slug}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '妖精漫画网',
              url: 'http://lvtianyuan.com/',
            },
          },
          breadcrumbJsonLd,
        ]}
      />

      <main className="pt-16">
        <section className="relative w-full h-[300px] md:h-[400px] overflow-hidden">
          <img
            src={heroImage}
            alt={category.name}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50" />
          <div className="absolute bottom-0 left-0 right-0 px-4 md:px-6 pb-10">
            <div className="max-w-[1280px] mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '分类浏览' },
                  { label: category.name },
                ]}
              />
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-background-50 mb-2">
                {category.name}
                <span className="text-accent-500 ml-3">韩国漫画</span>
              </h1>
              <p className="text-sm text-background-50/80 max-w-lg">{category.description}</p>
            </div>
          </div>
        </section>

        <section className="bg-background-100/70 border-b border-background-200/70">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-4">
            <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
              <span className="text-xs text-foreground-500 shrink-0 mr-1">分类切换：</span>
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    cat.slug === category.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 hover:bg-primary-50 hover:text-primary-600 border border-background-200/70'
                  }`}
                >
                  {cat.name}
                  <span className={`ml-1 text-[10px] ${cat.slug === category.slug ? 'text-background-50/70' : 'text-foreground-400'}`}>
                    {cat.comicCount}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-10">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-lg font-bold text-foreground-950">
              共 <span className="text-accent-500">{comics.length}</span> 部作品
            </h2>
            <div className="flex items-center gap-2">
              <span className="text-xs text-foreground-500">排序：</span>
              <button className="px-3 py-1.5 rounded-full text-xs font-medium bg-primary-500 text-background-50 whitespace-nowrap cursor-pointer">人气排行</button>
              <button className="px-3 py-1.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors whitespace-nowrap cursor-pointer">最近更新</button>
            </div>
          </div>

          {comics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {comics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-foreground-500">暂无该分类的漫画</p>
            </div>
          )}
        </section>
      </main>
    </>
  );
}